/**
 * @packageDocumentation
 * @module      CorPageAuditLogs.ts
 * @description The Audit Logs page for this application.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CorPageAuditLogs_1;
import { css, customElement, html, LitElement, internalProperty, property } from 'lit-element';
import CloudApi from "./CloudApi";
import Fetcher from "./Fetcher";
import AuditLogType from "./AuditLogType";
import { paddingStyles, universalStyles } from "./CorMain";
import CorThemeSelector from "./CorThemeSelector";
let corPageAuditLogsFetcher = new Fetcher();
/** Object through which all API access occurs. In unit tests, overwrite this with a mock. */
export { corPageAuditLogsFetcher };
/** The Audit Logs page, comprised of a menu on the left and a card for the selected log on the right. */
let CorPageAuditLogs = CorPageAuditLogs_1 = class CorPageAuditLogs extends LitElement {
    static get styles() {
        return css `
          ${universalStyles}
          ${paddingStyles}
          
          #card {
            width: 100%;
          }
          
          sh-text {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }
        `;
    }
    get currentAuditLogType() {
        return this.auditLogTypes[this.currentAuditLogTypeIndex];
    }
    get changesMadeColumns() {
        return this.currentAuditLogType.hasSecondaryItem ? 6 : 8;
    }
    get itemColumns() {
        return this.currentAuditLogType.hasSecondaryItem ? 4 : 5;
    }
    get currentTheme() {
        return CorThemeSelector.getTheme();
    }
    chooseReport(index) {
        this.currentAuditLogTypeIndex = index;
        this.currentPage = 1;
        this.getRecords();
    }
    getRecords() {
        new CloudApi(corPageAuditLogsFetcher, this).getAuditLog(this.currentAuditLogType.urlKey, this.currentPage - 1, this.itemsPerPage)
            .then((result) => {
            this.auditLogLines = result;
        });
    }
    constructor() {
        super();
        this.auditLogTypes = [
            new AuditLogType('Robotic Network', 'Changes Made', 'Robotic Network Name', 'Modified By', 'roboticNetwork'),
            new AuditLogType('System', 'Changes Made', 'System Name', 'Modified By', 'system'),
            new AuditLogType('Session', 'Event', 'Primary System', 'Initiator', 'session', 'Secondary System', 'Event Status'),
        ];
        this.currentPage = 1;
        this.currentAuditLogTypeIndex = 0;
        this.auditLogLines = [];
        this.itemsPerPage = 100;
        this.Pages = 1;
        this.chooseReport(0);
    }
    changeCurrentPage(e) {
        this.currentPage = Number(e.target.currentPage);
        this.getRecords();
    }
    calculatePages() {
        return (Math.ceil(CorPageAuditLogs_1.auditLogItemCount / this.itemsPerPage));
    }
    /** Render the component as part of Lit-Element's lifecycle. */
    render() {
        const self = this;
        this.Pages = this.calculatePages();
        return html `
            <sh-page id="ThePage" theme="${this.currentTheme}">
                <sh-pane slot="left" id="list">
                    ${this.auditLogTypes.map((t, index) => html `
                                <sh-menu-item
                                        label=${t.displayName}
                                        value=${t.urlKey}
                                        ?active="${index === self.currentAuditLogTypeIndex}"
                                        @click=${() => self.chooseReport(index)}
                                >
                                </sh-menu-item>`)}
                </sh-pane>
                <sh-card id="card" label="${this.currentAuditLogType.displayName} Maintenance Log">
                    <sh-text class="paddingAll" size="body-2">Results (${CorPageAuditLogs_1.auditLogItemCount})</sh-text>
                    <sh-table id="systems-table">
                        <sh-table-row slot="header">
                            <sh-table-head columns="4" unsortable>Date</sh-table-head>
                            <sh-table-head columns="2" unsortable>Event ID</sh-table-head>
                            <sh-table-head columns="${this.changesMadeColumns}" unsortable>
                                ${this.currentAuditLogType.summaryHeader}
                            </sh-table-head>
                            <sh-table-head columns="${this.itemColumns}" unsortable>
                                ${this.currentAuditLogType.itemHeader}
                            </sh-table-head>
                            ${this.currentAuditLogType.hasSecondaryItem ?
            html `
                                    <sh-table-head columns="${this.itemColumns}" unsortable>
                                        ${this.currentAuditLogType.secondaryItemHeader}
                                    </sh-table-head>` :
            html ``}
                            <sh-table-head columns="5" unsortable>
                                ${this.currentAuditLogType.initiatorHeader}
                            </sh-table-head>
                            ${this.currentAuditLogType.hasSuccess ?
            html `
                                    <sh-table-head columns="3" unsortable>
                                        ${this.currentAuditLogType.successHeader}
                                    </sh-table-head>` :
            html ``}
                        </sh-table-row>
                        ${this.auditLogLines.map((line, index) => html `
                            <sh-table-row>
                                <sh-table-cell columns="4" title="${line.eventTime.toLocaleString()}">
                                    <sh-text>${line.eventTime.toLocaleString()}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="2" title="${line.eventId}">
                                    <sh-text>${line.eventId}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="${this.changesMadeColumns}" title="${line.summary}">
                                    <sh-text>${line.summary}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="${this.itemColumns}" title="${line.itemName}">
                                    <sh-text>${line.itemName}</sh-text>
                                </sh-table-cell>
                                ${this.currentAuditLogType.hasSecondaryItem ?
            html `
                                        <sh-table-cell columns="${this.itemColumns}" title="${line.secondaryItemName}">
                                            <sh-text>${line.secondaryItemName}</sh-text>
                                        </sh-table-cell>` :
            html ``}
                                <sh-table-cell columns="5" title="${line.principalUsername || "A pg_cron job running in the database"}">
                                    <sh-text>${line.principalUsername || "(Background Job)"}</sh-text>
                                </sh-table-cell>
                                ${this.currentAuditLogType.hasSuccess ?
            html `
                                        <sh-table-cell columns="3" title="${line.success}">
                                            <sh-text>${line.success ? 'Succeeded' : 'Failed'}</sh-text>
                                        </sh-table-cell>` :
            html ``}                                
                            </sh-table-row>
                        `)}
                    </sh-table>
                    <sh-pagination pages="${this.Pages}" current-page="${this.currentPage}" @click="${this.changeCurrentPage}"> </sh-pagination>
                </sh-card>
            </sh-page>`;
    }
};
CorPageAuditLogs.auditLogItemCount = 1;
__decorate([
    internalProperty()
], CorPageAuditLogs.prototype, "auditLogTypes", void 0);
__decorate([
    property()
], CorPageAuditLogs.prototype, "currentPage", void 0);
__decorate([
    internalProperty()
], CorPageAuditLogs.prototype, "currentAuditLogTypeIndex", void 0);
__decorate([
    internalProperty()
], CorPageAuditLogs.prototype, "currentAuditLogType", null);
__decorate([
    internalProperty()
], CorPageAuditLogs.prototype, "auditLogLines", void 0);
__decorate([
    internalProperty()
], CorPageAuditLogs.prototype, "changesMadeColumns", null);
__decorate([
    internalProperty()
], CorPageAuditLogs.prototype, "itemColumns", null);
__decorate([
    internalProperty()
], CorPageAuditLogs.prototype, "currentTheme", null);
CorPageAuditLogs = CorPageAuditLogs_1 = __decorate([
    customElement('cor-page-audit-logs')
], CorPageAuditLogs);
export { CorPageAuditLogs };
//# sourceMappingURL=CorPageAuditLogs.js.map